/**
 * Progressive Image Loader
 * Sprint 4I: Fast image loading with progressive enhancement
 * 
 * FEATURES:
 * - Loads sm → md → lg progressively
 * - Lazy loading (only loads when visible)
 * - Blur-up effect (like Medium.com, Foodpanda)
 * - Automatic fallback
 * - Client-side caching
 */

import { useState, useEffect, useRef } from 'react';
import { ImageIcon } from 'lucide-react';
import type { ProductImage } from '@/lib/constants';

interface ProgressiveImageProps {
  image: ProductImage | null;
  alt: string;
  className?: string;
  lazy?: boolean;
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
}

export function ProgressiveImage({
  image,
  alt,
  className = '',
  lazy = true,
  size = 'md',
  onClick,
}: ProgressiveImageProps) {
  const [currentSrc, setCurrentSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isVisible, setIsVisible] = useState(!lazy);
  const imgRef = useRef<HTMLDivElement>(null);

  // Lazy loading with Intersection Observer
  useEffect(() => {
    if (!lazy || !imgRef.current) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px',
      }
    );

    observer.observe(imgRef.current);

    return () => observer.disconnect();
  }, [lazy]);

  // Progressive loading: sm → md → lg
  useEffect(() => {
    if (!image || !isVisible) return;

    let mounted = true;
    setIsLoading(true);
    setError(false);

    const loadSequence =
      size === 'sm'
        ? [image.smUrl]
        : size === 'md'
        ? [image.smUrl, image.mdUrl]
        : [image.smUrl, image.mdUrl, image.lgUrl];

    const loadNext = async (index: number) => {
      if (!mounted || index >= loadSequence.length) {
        setIsLoading(false);
        return;
      }

      const url = loadSequence[index];

      try {
        await preloadImage(url);
        
        if (mounted) {
          setCurrentSrc(url);
          
          if (index < loadSequence.length - 1) {
            setTimeout(() => loadNext(index + 1), 100);
          } else {
            setIsLoading(false);
          }
        }
      } catch (err) {
        console.error(`Failed to load image: ${url}`, err);
        if (mounted && index === 0) {
          setError(true);
          setIsLoading(false);
        } else if (mounted) {
          loadNext(index + 1);
        }
      }
    };

    loadNext(0);

    return () => {
      mounted = false;
    };
  }, [image, isVisible, size]);

  if (!image || error) {
    return (
      <div
        ref={imgRef}
        className={`flex items-center justify-center bg-gray-100 ${className}`}
        onClick={onClick}
      >
        <ImageIcon className="w-8 h-8 text-gray-400" />
      </div>
    );
  }

  return (
    <div
      ref={imgRef}
      className={`relative overflow-hidden ${className}`}
      onClick={onClick}
    >
      {currentSrc && (
        <img
          src={currentSrc}
          alt={alt}
          className={`w-full h-full object-cover transition-all duration-300 ${
            isLoading ? 'blur-sm scale-105' : 'blur-0 scale-100'
          }`}
          loading={lazy ? 'lazy' : 'eager'}
        />
      )}

      {isLoading && currentSrc && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/10">
          <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
}

function preloadImage(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`Failed to load ${url}`));
    img.src = url;
  });
}








